/**
 * @file frame.ts
 * @description GCOME_AP 64 字节 AP 帧编解码。
 */

import { GCOME_AP_REPORT } from './cmd'
import type { GcomeApFrame, GcomeApFrameRequest } from './types'

const BYTE_MAX = 0xff
const ADDRESS_MAX = 0xffff

export function encodeGcomeApFrame(request: GcomeApFrameRequest): GcomeApFrame {
  const command = normalizeByte(request.command, 'command')
  const key = normalizeByte(request.key ?? 0, 'key')
  const reserved = normalizeByte(request.reserved ?? 0, 'reserved')
  const address = normalizeAddress(request.address ?? 0)
  const data = normalizePayload(request.data ?? [])
  const raw = new Uint8Array(GCOME_AP_REPORT.frameSize)

  raw[0] = GCOME_AP_REPORT.hostFlag
  raw[1] = command
  raw[2] = key
  raw[4] = data.length
  raw[5] = address & BYTE_MAX
  raw[6] = (address >> 8) & BYTE_MAX
  raw[7] = reserved
  raw.set(data, GCOME_AP_REPORT.headerSize)

  raw[3] = calculateGcomeApChecksum(raw, data.length)
  xorFrameBody(raw, key)

  return {
    flag: GCOME_AP_REPORT.hostFlag,
    command,
    key,
    checksum: raw[3] ^ key,
    length: data.length,
    address,
    reserved,
    data,
    raw,
  }
}

export function decodeGcomeApFrame(
  input: DataView | Uint8Array,
  expectedFlag = GCOME_AP_REPORT.deviceFlag,
): GcomeApFrame {
  const raw = dataViewToBytes(input)
  if (raw.length !== GCOME_AP_REPORT.frameSize) {
    throw new Error(`GCOME_AP 帧长度必须是 64 字节，当前为 ${raw.length}。`)
  }

  const plain = raw.slice()
  const key = plain[2]
  xorFrameBody(plain, key)

  const flag = plain[0]
  if (flag !== expectedFlag) {
    throw new Error(`GCOME_AP FLAG 不匹配，期望 0x${hex(expectedFlag)}，收到 0x${hex(flag)}。`)
  }

  const length = plain[4]
  if (length > GCOME_AP_REPORT.payloadSize) {
    throw new Error(`GCOME_AP 数据长度超出 56 字节：${length}。`)
  }

  const checksum = plain[3]
  const actualChecksum = calculateGcomeApChecksum(plain, length)
  if (checksum !== actualChecksum) {
    throw new Error(
      `GCOME_AP CS 校验失败，期望 0x${hex(actualChecksum)}，收到 0x${hex(checksum)}。`,
    )
  }

  return {
    flag,
    command: plain[1],
    key,
    checksum,
    length,
    address: plain[5] | (plain[6] << 8),
    reserved: plain[7],
    data: plain.slice(GCOME_AP_REPORT.headerSize, GCOME_AP_REPORT.headerSize + length),
    raw,
  }
}

export function calculateGcomeApChecksum(frame: Uint8Array, length = frame[4]): number {
  const end = GCOME_AP_REPORT.headerSize + normalizePayloadLength(length)
  let sum = 0
  for (let i = 4; i < end; i++) {
    sum = (sum + frame[i]) & BYTE_MAX
  }
  return sum
}

export function bytesToHex(bytes: Uint8Array): string {
  return Array.from(bytes)
    .map((item) => item.toString(16).padStart(2, '0').toUpperCase())
    .join(' ')
}

export function hexToBytes(input: string): Uint8Array {
  const normalized = input.trim()
  if (!normalized) return new Uint8Array()

  const parts = normalized.split(/[\s,;]+/).filter(Boolean)
  const bytes = parts.map((part) => {
    const value = Number.parseInt(part.replace(/^0x/i, ''), 16)
    return normalizeByte(value, `hex byte "${part}"`)
  })
  return new Uint8Array(bytes)
}

function dataViewToBytes(input: DataView | Uint8Array): Uint8Array {
  if (input instanceof Uint8Array) {
    return input.slice()
  }
  return new Uint8Array(input.buffer, input.byteOffset, input.byteLength).slice()
}

function xorFrameBody(bytes: Uint8Array, key: number): void {
  if (key === 0) return
  // 协议约定 KEY 之后的字节参与异或，包含 CS/LEN/ADDR/Reserved/DATA。
  for (let i = 3; i < bytes.length; i++) {
    bytes[i] ^= key
  }
}

function normalizePayload(input: Uint8Array | number[]): Uint8Array {
  const payload = input instanceof Uint8Array ? input.slice() : new Uint8Array(input)
  if (payload.length > GCOME_AP_REPORT.payloadSize) {
    throw new Error(`GCOME_AP 单帧数据最多 56 字节，当前为 ${payload.length}。`)
  }
  for (const value of payload) {
    normalizeByte(value, 'data')
  }
  return payload
}

function normalizePayloadLength(length: number): number {
  if (!Number.isInteger(length) || length < 0 || length > GCOME_AP_REPORT.payloadSize) {
    throw new Error(`GCOME_AP LEN 必须是 0-56 的整数，当前为 ${length}。`)
  }
  return length
}

function normalizeAddress(address: number): number {
  if (!Number.isInteger(address) || address < 0 || address > ADDRESS_MAX) {
    throw new Error(`GCOME_AP address 必须是 0-65535 的整数，当前为 ${address}。`)
  }
  return address
}

function normalizeByte(value: number, field: string): number {
  if (!Number.isInteger(value) || value < 0 || value > BYTE_MAX) {
    throw new Error(`GCOME_AP ${field} 必须是 0-255 的整数，当前为 ${value}。`)
  }
  return value
}

function hex(value: number): string {
  return value.toString(16).padStart(2, '0').toUpperCase()
}
